import {
  computed,
  defineComponent,
  inject,
  ref,
  toRaw,
  unref,
  watch,
} from 'vue'
import { get } from 'lodash-unified'
import { isObject, isUndefined } from '@element-plus/utils'
import {
  DynamicSizeList,
  FixedSizeList,
} from '@element-plus/components/virtual-list'
import { useLocale, useNamespace } from '@element-plus/hooks'
import { EVENT_CODE } from '@element-plus/constants'
import { ElRadioButton, ElRadioGroup } from '@element-plus/components/radio'
import ElButton, { ElButtonGroup } from '@element-plus/components/button'
import GroupItem from './group-item.vue'
import OptionItem from './option-item.vue'
import { useProps } from './useProps'

import { selectV2InjectionKey } from './token'

import type { ItemProps } from '@element-plus/components/virtual-list'
import type { FilterType, Option, OptionItemProps } from './select.types'

export default defineComponent({
  name: 'ElSelectDropdown',
  props: {
    loading: Boolean,
    data: {
      type: Array,
      required: true,
    },
    hoveringIndex: Number,
    width: Number,
  },
  setup(props, { slots, expose, emit }) {
    const select = inject(selectV2InjectionKey)!
    const ns = useNamespace('select')
    const { t } = useLocale()
    const { getLabel, getValue, getDisabled } = useProps(select.props)
    const { clearFiltedOptions, selectAll, selectInverse } = select
    const cachedHeights = ref<Array<number>>([])
    const selectedType = ref<FilterType>('all')
    const listRef = ref()

    const size = computed(() => props.data.length)
    watch(
      () => size.value,
      () => {
        select.tooltipRef.value.updatePopper?.()
      }
    )

    watch(
      () => selectedType.value,
      () => {
        emit('filter-type-change', selectedType.value)
      }
    )

    const isSized = computed(() =>
      isUndefined(select.props.estimatedOptionHeight)
    )
    const listProps = computed(() => {
      if (isSized.value) {
        return {
          itemSize: select.props.itemHeight,
        }
      }

      return {
        estimatedSize: select.props.estimatedOptionHeight,
        itemSize: (idx: number) => cachedHeights.value[idx],
      }
    })

    const contains = (arr: Array<any> = [], target: any) => {
      const {
        props: { valueKey },
      } = select

      if (!isObject(target)) {
        return arr.includes(target)
      }

      return (
        arr &&
        arr.some((item) => {
          return toRaw(get(item, valueKey)) === get(target, valueKey)
        })
      )
    }
    const isEqual = (selected: unknown, target: unknown) => {
      if (!isObject(target)) {
        return selected === target
      } else {
        const { valueKey } = select.props
        return get(selected, valueKey) === get(target, valueKey)
      }
    }

    const isItemSelected = (modelValue: any[] | any, target: Option) => {
      if (select.props.multiple) {
        return contains(modelValue, getValue(target))
      }
      return isEqual(modelValue, getValue(target))
    }

    const isItemDisabled = (modelValue: any[] | any, selected: boolean) => {
      const { disabled, multiple, multipleLimit } = select.props
      return (
        disabled ||
        (!selected &&
          (multiple
            ? multipleLimit > 0 && modelValue.length >= multipleLimit
            : false))
      )
    }

    const isItemHovering = (target: number) => props.hoveringIndex === target

    const scrollToItem = (index: number) => {
      const list = listRef.value as any
      if (list) {
        list.scrollToItem(index)
      }
    }

    const resetScrollTop = () => {
      const list = listRef.value as any
      if (list) {
        list.resetScrollTop()
      }
    }

    expose({
      listRef,
      isSized,
      selectedType,

      isItemDisabled,
      isItemHovering,
      isItemSelected,
      scrollToItem,
      resetScrollTop,
    })

    const Item = (itemProps: ItemProps<any>) => {
      const { index, data, style } = itemProps
      const sized = unref(isSized)
      const { itemSize, estimatedSize } = unref(listProps)
      const { modelValue } = select.props
      const { onSelect, onHover } = select
      const item = data[index]
      if (item.type === 'Group') {
        return (
          <GroupItem
            item={item}
            style={style}
            height={(sized ? itemSize : estimatedSize) as number}
          />
        )
      }

      const isSelected = isItemSelected(modelValue, item)
      const isDisabled = isItemDisabled(modelValue, isSelected)
      const isHovering = isItemHovering(index)
      return (
        <OptionItem
          {...itemProps}
          selected={isSelected}
          disabled={getDisabled(item) || isDisabled}
          created={!!item.created}
          hovering={isHovering}
          item={item}
          onSelect={onSelect}
          onHover={onHover}
        >
          {{
            default: (props: OptionItemProps) =>
              slots.default?.(props) || <span>{getLabel(item)}</span>,
          }}
        </OptionItem>
      )
    }

    // computed
    const { onKeyboardNavigate, onKeyboardSelect } = select

    const onForward = () => {
      onKeyboardNavigate('forward')
    }

    const onBackward = () => {
      onKeyboardNavigate('backward')
    }

    const onEscOrTab = () => {
      select.expanded = false
    }

    const onKeydown = (e: KeyboardEvent) => {
      const { code } = e
      const { tab, esc, down, up, enter } = EVENT_CODE
      if (code !== tab) {
        e.preventDefault()
        e.stopPropagation()
      }

      switch (code) {
        case tab:
        case esc: {
          onEscOrTab()
          break
        }
        case down: {
          onForward()
          break
        }
        case up: {
          onBackward()
          break
        }
        case enter: {
          onKeyboardSelect()
          break
        }
      }
    }

    const onSelectAll = () => {
      selectAll()
      // emit('header-shortcut-handle', 'all')
    }

    const onSelectInverse = () => {
      selectInverse()
      // emit('header-shortcut-handle', 'invert')
    }

    const onClear = () => {
      clearFiltedOptions()
      // emit('header-shortcut-handle', 'clear')
    }

    return () => {
      const { data, width } = props
      const { height, multiple, scrollbarAlwaysOn, headerShortcut } =
        select.props

      const List = unref(isSized) ? FixedSizeList : DynamicSizeList
      return (
        <div
          class={[ns.b('dropdown'), ns.is('multiple', multiple)]}
          style={{
            minWidth: `${width}px`,
          }}
        >
          {slots.header?.()}
          {headerShortcut && multiple ? (
            <div class={ns.be('dropdown', 'header-shortcut')}>
              <div class={ns.be('dropdown', 'header-shortcut-checktype')}>
                <ElRadioGroup
                  modelValue={selectedType.value}
                  size="small"
                  onUpdate:modelValue={(value: any) => {
                    selectedType.value = value
                  }}
                >
                  <ElRadioButton label="all">
                    {t('el.select.all')}
                  </ElRadioButton>
                  <ElRadioButton label="selected">
                    {t('el.select.selected')}
                  </ElRadioButton>
                </ElRadioGroup>
              </div>
              <div class={ns.be('dropdown', 'header-shortcut-action')}>
                <ElButtonGroup size="small">
                  <ElButton type="text" onClick={onSelectAll}>
                    {t('el.select.selectAll')}
                  </ElButton>
                  <ElButton type="text" onClick={onSelectInverse}>
                    {t('el.select.invertSelection')}
                  </ElButton>
                  <ElButton type="text" onClick={onClear}>
                    {t('el.select.emptyAll')}
                  </ElButton>
                </ElButtonGroup>
              </div>
            </div>
          ) : null}
          {slots.loading?.() || slots.empty?.() || (
            <List
              ref={listRef}
              {...unref(listProps)}
              className={ns.be('dropdown', 'list')}
              scrollbarAlwaysOn={scrollbarAlwaysOn}
              data={data}
              height={height}
              width={'100%'}
              total={data.length}
              // @ts-ignore - dts problem
              onKeydown={onKeydown}
            >
              {{
                default: (props: ItemProps<any>) => <Item {...props} />,
              }}
            </List>
          )}
          {slots.footer?.()}
        </div>
      )
    }
  },
})
